import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  GetApp as DownloadIcon,
  Assignment as DocumentIcon,
  CheckCircle as CheckIcon,
  Business as BusinessIcon,
  Description as TemplateIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Update as UpdateIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface Template {
  category: string;
  name: string;
  description: string;
  fileType: string;
  lastUpdated: string;
  downloads: number;
  tags: string[];
  njSpecific: boolean;
  popular: boolean;
}

const titleTemplates: Template[] = [
  {
    category: 'Title Commitments',
    name: 'Standard Title Commitment - Residential',
    description: 'ALTA Form Title Commitment for single-family residential properties in New Jersey',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-06-12',
    downloads: 1847,
    tags: ['ALTA', 'Residential', 'Standard'],
    njSpecific: true,
    popular: true,
  },
  {
    category: 'Title Commitments',
    name: 'Commercial Title Commitment',
    description: 'Comprehensive title commitment template for commercial real estate transactions',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-05-22',
    downloads: 892,
    tags: ['ALTA', 'Commercial', 'Extended'],
    njSpecific: true,
    popular: true,
  },
  {
    category: 'Title Policies',
    name: "ALTA Owner's Policy - 2006",
    description: "Standard ALTA Owner's Title Insurance Policy with New Jersey modifications",
    fileType: 'PDF (.pdf)',
    lastUpdated: '2026-05-06',
    downloads: 2156,
    tags: ['ALTA', 'Owners Policy', '2006'],
    njSpecific: true,
    popular: true,
  },
  {
    category: 'Title Policies',
    name: "ALTA Loan Policy - 2006",
    description: "Standard ALTA Loan Title Insurance Policy for lender protection",
    fileType: 'PDF (.pdf)',
    lastUpdated: '2026-05-08',
    downloads: 1923,
    tags: ['ALTA', 'Loan Policy', '2006'],
    njSpecific: true,
    popular: true,
  },
  {
    category: 'Endorsements',
    name: 'ALTA 6 - Variable Rate Mortgage',
    description: 'Endorsement for adjustable rate mortgage loans',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-06-01',
    downloads: 634,
    tags: ['Endorsement', 'ARM', 'ALTA 6'],
    njSpecific: false,
    popular: false,
  },
  {
    category: 'Endorsements',
    name: 'ALTA 8.1 - Environmental Protection',
    description: 'Environmental protection lien endorsement',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-05-27',
    downloads: 445,
    tags: ['Endorsement', 'Environmental', 'ALTA 8.1'],
    njSpecific: false,
    popular: false,
  },
  {
    category: 'Closing Documents',
    name: 'New Jersey Settlement Statement (HUD-1)',
    description: 'Standardized settlement statement for NJ real estate closings',
    fileType: 'Excel Spreadsheet (.xlsx)',
    lastUpdated: '2026-06-15',
    downloads: 1567,
    tags: ['HUD-1', 'Settlement', 'Closing'],
    njSpecific: true,
    popular: true,
  },
  {
    category: 'Closing Documents',
    name: 'Deed Templates - New Jersey',
    description: 'Complete set of deed templates including warranty, quit claim, and special warranty deeds',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-05-19',
    downloads: 2034,
    tags: ['Deed', 'Warranty', 'Quit Claim'],
    njSpecific: true,
    popular: true,
  },
  {
    category: 'Forms & Notices',
    name: 'Title Examination Checklist',
    description: 'Comprehensive checklist for thorough title examination procedures',
    fileType: 'PDF (.pdf)',
    lastUpdated: '2026-06-18',
    downloads: 723,
    tags: ['Checklist', 'Examination', 'Procedures'],
    njSpecific: true,
    popular: false,
  },
  {
    category: 'Forms & Notices',
    name: 'Defect Notice Template',
    description: 'Standardized notice for title defects and required curative action',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-05-13',
    downloads: 398,
    tags: ['Notice', 'Defects', 'Curative'],
    njSpecific: true,
    popular: false,
  },
  {
    category: 'Legal Forms',
    name: 'Power of Attorney - Real Estate',
    description: 'Limited power of attorney specifically for real estate transactions',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-06-05',
    downloads: 567,
    tags: ['POA', 'Legal', 'Authorization'],
    njSpecific: true,
    popular: false,
  },
  {
    category: 'Legal Forms',
    name: 'Affidavit of Title',
    description: 'Standard affidavit of title for New Jersey real estate transactions',
    fileType: 'Word Document (.docx)',
    lastUpdated: '2026-05-30',
    downloads: 834,
    tags: ['Affidavit', 'Legal', 'Title'],
    njSpecific: true,
    popular: false,
  },
];

const templateCategories = [
  'All Categories',
  'Title Commitments',
  'Title Policies',
  'Endorsements',
  'Closing Documents',
  'Forms & Notices',
  'Legal Forms'
];

const TitleTemplates: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [expandedTemplate, setExpandedTemplate] = useState<string | false>(false);

  const filteredTemplates = titleTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All Categories' || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleTemplateChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedTemplate(isExpanded ? panel : false);
  };

  const popularTemplates = titleTemplates.filter(template => template.popular);

  return (
    <>
      <Helmet>
        <title>Title Templates & Documents | Professional Resources | MAD Title Agency</title>
        <meta
          name="description"
          content="Professional title insurance templates, ALTA forms, and standardized documents for New Jersey title professionals. Download commitment templates, policies, and closing forms."
        />
        <meta
          name="keywords"
          content="title templates, ALTA forms, title commitment template, title policy template, NJ title documents, title insurance forms, professional title resources"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Title Templates
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#065f46', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Templates"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Title Templates & Documents
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Professional ALTA forms, templates, and standardized documents for New Jersey title professionals
              </Typography>
              <Alert severity="info" sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: 'none' }}>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  All templates are regularly updated to reflect current ALTA standards and New Jersey requirements.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <TemplateIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {titleTemplates.length} Templates
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Professional Quality
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Popular Templates */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#065f46' }}>
            Most Popular Templates
          </Typography>
          <Grid container spacing={4}>
            {popularTemplates.map((template, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: '1px solid #e2e8f0', position: 'relative' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StarIcon sx={{ color: '#f59e0b', mr: 1 }} />
                      <Chip label="Popular" size="small" sx={{ backgroundColor: '#f59e0b', color: 'white' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {template.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                      {template.tags.map((tag, tagIndex) => (
                        <Chip key={tagIndex} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {template.downloads.toLocaleString()} downloads
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {template.fileType}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                        width: '100%',
                      }}
                    >
                      Download Template
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Search and Filter */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 3, fontWeight: 600, color: '#065f46' }}>
            All Templates & Documents
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search templates, descriptions, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {templateCategories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    onClick={() => setSelectedCategory(category)}
                    color={selectedCategory === category ? 'primary' : 'default'}
                    variant={selectedCategory === category ? 'filled' : 'outlined'}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: selectedCategory === category ? 'primary.dark' : 'grey.100' },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Templates List */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#065f46' }}>
            Available Templates ({filteredTemplates.length} found)
          </Typography>

          {filteredTemplates.map((template, index) => (
            <Accordion
              key={index}
              expanded={expandedTemplate === `template${index}`}
              onChange={handleTemplateChange(`template${index}`)}
              sx={{ mb: 2, border: '1px solid #e2e8f0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {template.name}
                      </Typography>
                      {template.popular && (
                        <StarIcon sx={{ color: '#f59e0b', ml: 1, fontSize: 20 }} />
                      )}
                      {template.njSpecific && (
                        <Chip 
                          label="NJ Specific" 
                          size="small" 
                          sx={{ ml: 1, backgroundColor: '#065f46', color: 'white' }} 
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end', mr: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {template.category}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.downloads.toLocaleString()} downloads
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Template Details
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <DocumentIcon sx={{ color: '#065f46' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="File Type"
                          secondary={template.fileType}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <UpdateIcon sx={{ color: '#065f46' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Last Updated"
                          secondary={new Date(template.lastUpdated).toLocaleDateString()}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <VerifiedIcon sx={{ color: '#065f46' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Compliance"
                          secondary={template.njSpecific ? "New Jersey specific requirements included" : "Standard ALTA form"}
                        />
                      </ListItem>
                    </List>
                    
                    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
                      Tags & Categories
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {template.tags.map((tag, tagIndex) => (
                        <Chip
                          key={tagIndex}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#065f46', color: '#065f46' }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<DownloadIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                          width: '100%',
                          mb: 2,
                        }}
                      >
                        Download Template
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Downloaded {template.downloads.toLocaleString()} times
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Usage Guidelines */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#065f46' }}>
            Template Usage Guidelines
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#f0fdf4' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  ✅ Best Practices
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Review all templates before use"
                      secondary="Ensure templates meet your specific transaction requirements"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Customize for local requirements"
                      secondary="Adapt templates to specific county or municipal requirements"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Stay current with updates"
                      secondary="Download latest versions to ensure compliance"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Verify with legal counsel"
                      secondary="Have attorney review complex or unusual transactions"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#fef2f2' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  ⚠️ Important Notes
                </Typography>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Disclaimer:</strong> These templates are provided for reference only. 
                    Always consult with qualified legal counsel for specific transactions.
                  </Typography>
                </Alert>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Regular Updates Required"
                      secondary="Laws and regulations change - always use current versions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Professional Review Recommended"
                      secondary="Have experienced title professionals review complex transactions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Local Variations Apply"
                      secondary="Some counties have additional requirements not in templates"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Related Resources */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#065f46' }}>
            Related Professional Resources
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <DocumentIcon sx={{ fontSize: 48, color: '#065f46', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Search Manual
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Professional procedures for comprehensive title examination.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-search-manual"
                  fullWidth
                >
                  View Manual
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CheckIcon sx={{ fontSize: 48, color: '#065f46', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  ALTA Compliance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Best practices checklist and compliance tracking.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/alta-compliance-checklist"
                  fullWidth
                >
                  View Checklist
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <BusinessIcon sx={{ fontSize: 48, color: '#065f46', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Rate Calculator
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Calculate accurate title insurance premiums.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/nj-title-rate-calculator"
                  fullWidth
                >
                  Calculate Rates
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f0fdf4', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Need Custom Templates?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Our professional team can create customized templates for your specific business needs.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Request Custom Templates
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="mailto:madtitleagency@gmail.com"
                sx={{
                  borderColor: '#065f46',
                  color: '#065f46',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Professional Services
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TitleTemplates; 